import { clsx } from 'clsx';
import { ReactNode } from 'react';
import LogoSvg from '../../../../assets/logo.svg?react';
import { AuthFormBoxLayout } from './AuthFormBoxLayout';
import { FlowShell } from './FlowShell';
import * as styles from './AuthLayout.css';
import * as registerStyles from '../register.view.css';

type AuthLayoutPreset = 'register-single-column' | 'register-two-column';

type AuthLayoutProps = {
  preset?: AuthLayoutPreset;
  children?: ReactNode;
  content?: ReactNode;
  rightColumn?: ReactNode;
  withBox?: boolean;
  header?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  cta?: ReactNode;
  footer?: ReactNode;
  align?: 'center' | 'start';
  bodyKey?: string;
  stackClassName?: string;
  className?: string;
  boxClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
  contentClassName?: string;
  ctaClassName?: string;
  footerClassName?: string;
  belowContent?: ReactNode;
  onLogoClick?: () => void;
};

export const AuthLayout = ({
  preset,
  children,
  content,
  rightColumn,
  withBox = true,
  header,
  title,
  subtitle,
  cta,
  footer,
  align = 'center',
  bodyKey,
  stackClassName,
  className,
  boxClassName,
  headerClassName,
  bodyClassName,
  contentClassName,
  ctaClassName,
  footerClassName,
  belowContent,
  onLogoClick,
}: AuthLayoutProps) => {
  const hasTwoColumns = Boolean(rightColumn);
  const resolvedContent = content ?? children;
  const resolvedBoxClassName = clsx(
    preset === 'register-single-column' && registerStyles.singleColumnBox,
    preset === 'register-two-column' && registerStyles.twoColumnBox,
    className,
    boxClassName,
  );
  const resolvedHeaderClassName = clsx(
    preset === 'register-single-column' && registerStyles.singleColumnHeader,
    headerClassName,
  );
  const resolvedBodyClassName = clsx(
    preset === 'register-single-column' && registerStyles.singleColumnBody,
    preset === 'register-single-column' && registerStyles.bodyStepTransition,
    preset === 'register-two-column' && registerStyles.centeredBody,
    bodyClassName,
  );
  const resolvedContentClassName = clsx(
    (preset === 'register-single-column' || preset === 'register-two-column') &&
      registerStyles.codeStepContent,
    contentClassName,
  );
  const resolvedFooterClassName = clsx(
    preset === 'register-single-column' && registerStyles.singleColumnFooter,
    footerClassName,
  );
  const shouldShowDotsInShell =
    hasTwoColumns || preset === 'register-single-column';
  const resolvedHeader =
    header === undefined ? (
      <LogoSvg
        className={
          preset === 'register-single-column'
            ? registerStyles.singleColumnBrandLogo
            : preset === 'register-two-column'
              ? registerStyles.brandLogo
              : styles.defaultHeaderLogo
        }
      />
    ) : (
      header
    );
  const resolvedHeaderWithAction = onLogoClick ? (
    <button
      type="button"
      className={styles.logoButton}
      data-testid="auth-logo-button"
      aria-label="Go to flow start"
      onClick={onLogoClick}
    >
      {resolvedHeader}
    </button>
  ) : (
    resolvedHeader
  );

  return (
    <FlowShell
      layoutMode={hasTwoColumns ? 'two-column' : 'single-column'}
      showRightDots={shouldShowDotsInShell}
      mainContent={
        <div className={clsx(styles.mainStack, stackClassName)}>
          <AuthFormBoxLayout
            boxed={withBox}
            className={resolvedBoxClassName}
            header={
              <div className={styles.logoSlot}>{resolvedHeaderWithAction}</div>
            }
            headerClassName={resolvedHeaderClassName}
            title={title}
            subtitle={subtitle}
            content={resolvedContent}
            cta={cta}
            footer={footer}
            align={align}
            bodyKey={bodyKey}
            bodyClassName={resolvedBodyClassName}
            contentClassName={resolvedContentClassName}
            ctaClassName={ctaClassName}
            footerClassName={resolvedFooterClassName}
          />
          {belowContent}
        </div>
      }
      sideContent={rightColumn}
    />
  );
};
